import {
  formatDailyId,
  getDailyGameIndexForDailyId,
  parseDailyId,
} from './daily';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from './storage';
import { getPublicEnvValue } from './publicEnv';

export const DEFAULT_JIGSAW_DAILY_IMAGE_MAP: Record<string, string> = {
  '2026-02-03': '/assets/jigsaw/cat1.jpg',
  '2026-02-04': '/assets/jigsaw/jigsaw0.jpg',
  '2026-02-05': '/assets/jigsaw/jigsaw1.jpg',
  '2026-02-06': '/assets/jigsaw/jigsaw2.jpg',
  '2026-02-07': '/assets/jigsaw/jigsaw3.jpg',
  '2026-02-08': '/assets/jigsaw/jigsaw4.jpg',
  '2026-02-09': '/assets/jigsaw/jigsaw5.jpg',
  '2026-02-10': '/assets/jigsaw/jigsaw8.jpg',
  '2026-02-11': '/assets/jigsaw/jigsaw7.jpg',
  '2026-02-12': '/assets/jigsaw/costume1.jpg',
  '2026-02-13': '/assets/jigsaw/jigsaw9.jpg',
  '2026-02-14': '/assets/jigsaw/movie1.jpg',
  '2026-02-15': '/assets/jigsaw/jigsaw11.jpg',
  '2026-02-16': '/assets/jigsaw/jigsaw12.jpg',
  '2026-02-17': '/assets/jigsaw/jigsaw13.jpg',
  '2026-02-18': '/assets/jigsaw/jigsaw14.jpg',
  '2026-02-19': '/assets/jigsaw/sponge1.jpg',
  '2026-02-20': '/assets/jigsaw/office1.jpg',
  '2026-02-21': '/assets/jigsaw/cat2.jpg',
  '2026-02-22': '/assets/jigsaw/wolf1.jpg',
  '2026-02-23': '/assets/jigsaw/celeb1.jpg',
  '2026-02-24': '/assets/jigsaw/ucdavis1.jpg',
  '2026-03-01': '/assets/jigsaw/jigsaw15.jpeg',
  '2026-03-02': '/assets/jigsaw/jigsaw16.jpeg',
  '2026-03-03': '/assets/jigsaw/jigsaw17.jpeg',
  '2026-03-04': '/assets/jigsaw/jigsaw18.jpeg',
  '2026-03-05': '/assets/jigsaw/jigsaw19.jpeg',
  '2026-03-06': '/assets/jigsaw/yak-match-preview.jpg',
  '2026-03-07': '/assets/jigsaw/jigsaw21.gif',
  '2026-03-08': '/assets/jigsaw/jigsaw22.png',
  '2026-03-09': '/assets/jigsaw/jigsaw23.png',
  '2026-03-10': '/assets/jigsaw/jigsaw24.jpeg',
  '2026-03-11': '/assets/jigsaw/jigsaw25.jpeg',
  '2026-03-12': '/assets/jigsaw/jigsaw26.jpeg',
  '2026-03-13': '/assets/jigsaw/jigsaw27.jpeg',
  '2026-03-14': '/assets/jigsaw/jigsaw28.jpeg',
  '2026-03-15': '/assets/jigsaw/jigsaw29.jpeg',
  '2026-03-16': '/assets/jigsaw/jigsaw30.jpeg',
  '2026-03-17': '/assets/jigsaw/jigsaw31.jpeg',
  '2026-03-18': '/assets/jigsaw/jigsaw32.jpeg',
  '2026-03-19': '/assets/jigsaw/jigsaw33.png',
  '2026-03-20': '/assets/jigsaw/jigsaw34.jpeg',
  '2026-03-21': '/assets/jigsaw/jigsaw35.jpeg',
  '2026-03-22': '/assets/jigsaw/jigsaw36.jpeg',
  '2026-03-23': '/assets/jigsaw/jigsaw37.jpeg',
  '2026-03-24': '/assets/jigsaw/jigsaw38.jpeg',
  '2026-03-25': '/assets/jigsaw/jigsaw39.jpeg',
  '2026-03-26': '/assets/jigsaw/jigsaw40.jpeg',
  '2026-03-27': '/assets/jigsaw/jigsaw20.jpeg',
};

export const JIGSAW_ROTATION_OPTIONS = [0, 90, 180, 270] as const;

export type JigsawRotation = (typeof JIGSAW_ROTATION_OPTIONS)[number];

export interface JigsawImageRecord {
  id: string;
  title: string;
  alt: string;
  attribution: string;
  notes: string;
  assignedDates: string[];
  pinnedDates: string[];
  rotation: JigsawRotation;
  archived: boolean;
  originalKey: string;
  displayKey: string;
  thumbnailKey: string;
  sourceFilename: string;
  sourceContentType: string;
  width: number;
  height: number;
  fileSize: number;
  createdAt: string;
  updatedAt: string;
}

export interface JigsawManifest {
  version: 1;
  updatedAt: string;
  images: JigsawImageRecord[];
}

export interface JigsawAdminImageRecord extends JigsawImageRecord {
  displayUrl: string;
  thumbnailUrl: string;
  status: 'draft' | 'scheduled' | 'archived';
}

export interface JigsawAdminManifestResponse {
  updatedAt: string;
  images: JigsawAdminImageRecord[];
}

export interface JigsawPublicManifest {
  updatedAt: string;
  dailyImageMap: Record<string, string>;
  dailyImageIds?: Record<string, string>;
  images?: Array<{
    id: string;
    assignedDates: string[];
  }>;
}

export interface JigsawCatalog {
  updatedAt?: string;
  dailyImageMap: Record<string, string>;
  dailyImageIds: Record<string, string>;
  imageUrls: string[];
}

export const EMPTY_JIGSAW_MANIFEST: JigsawManifest = {
  version: 1,
  updatedAt: '',
  images: [],
};

const DAILY_ID_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MANIFEST_TIMEOUT_MS = 5000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_JIGSAW_IMAGE_URLS = Array.from(new Set(Object.values(DEFAULT_JIGSAW_DAILY_IMAGE_MAP)));
const JIGSAW_CACHE_REQUEST_ORIGIN = 'https://jigsaw-cache.local';
const JIGSAW_IMAGE_CACHE_NAME = 'jigsaw-image-cache-v1';
const JIGSAW_IMAGE_CACHE_METADATA_KEY = 'jigsaw-image-cache-metadata-v1';

export const JIGSAW_SCHEDULE_TIMEZONE = 'America/New_York';
export const JIGSAW_LOCKED_DAY_COUNT = 2;
export const JIGSAW_IMAGE_CACHE_TTL_MS = DAY_MS;

export interface ResolvedJigsawImage {
  dailyId: string;
  imageId: string;
  imageUrl: string;
  source: 'remote' | 'fallback';
}

interface JigsawImageCacheEntry {
  imageUrl: string;
  storedAt: number;
  expiresAt: number;
}

type JigsawImageCacheMetadata = Record<string, JigsawImageCacheEntry>;

export interface EnsureJigsawImagePersistedResult {
  dailyId: string;
  imageUrl: string;
  ok: boolean;
  fromCache: boolean;
  error?: string;
}

let cachedCatalog: JigsawCatalog | null = null;
let catalogPromise: Promise<JigsawCatalog> | null = null;

const normalizeOptionalText = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const uniqueStrings = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

export const isValidDailyId = (value: string) => DAILY_ID_PATTERN.test(value);

export const parseAssignedDatesInput = (value: string | string[]) => {
  const rawValues = Array.isArray(value) ? value : value.split(',');
  const normalized = rawValues
    .map((item) => item.trim())
    .filter(Boolean);

  const invalid = normalized.filter((item) => !isValidDailyId(item));
  return {
    assignedDates: uniqueStrings(normalized),
    invalidDates: uniqueStrings(invalid),
  };
};

export const deriveJigsawImageStatus = (record: Pick<JigsawImageRecord, 'archived' | 'assignedDates'>) => {
  if (record.archived) return 'archived' as const;
  if (record.assignedDates.length > 0) return 'scheduled' as const;
  return 'draft' as const;
};

export const normalizeJigsawRotation = (value: unknown): JigsawRotation => {
  const numericValue = typeof value === 'string' ? Number(value) : value;
  return JIGSAW_ROTATION_OPTIONS.includes(numericValue as JigsawRotation)
    ? (numericValue as JigsawRotation)
    : 0;
};

export const normalizeJigsawImageRecord = (record: Partial<JigsawImageRecord>): JigsawImageRecord => {
  const assignedDates = uniqueStrings(
    Array.isArray(record.assignedDates)
      ? record.assignedDates.filter(isValidDailyId)
      : [],
  );
  const pinnedDates = uniqueStrings(
    Array.isArray(record.pinnedDates)
      ? record.pinnedDates.filter((dailyId) => assignedDates.includes(dailyId))
      : [],
  );

  return {
    id: normalizeOptionalText(record.id),
    title: normalizeOptionalText(record.title),
    alt: normalizeOptionalText(record.alt),
    attribution: normalizeOptionalText(record.attribution),
    notes: normalizeOptionalText(record.notes),
    assignedDates,
    pinnedDates,
    rotation: normalizeJigsawRotation(record.rotation),
    archived: Boolean(record.archived),
    originalKey: normalizeOptionalText(record.originalKey),
    displayKey: normalizeOptionalText(record.displayKey),
    thumbnailKey: normalizeOptionalText(record.thumbnailKey),
    sourceFilename: normalizeOptionalText(record.sourceFilename),
    sourceContentType: normalizeOptionalText(record.sourceContentType),
    width: Number(record.width) > 0 ? Number(record.width) : 0,
    height: Number(record.height) > 0 ? Number(record.height) : 0,
    fileSize: Number(record.fileSize) > 0 ? Number(record.fileSize) : 0,
    createdAt: normalizeOptionalText(record.createdAt),
    updatedAt: normalizeOptionalText(record.updatedAt),
  };
};

export const normalizeJigsawManifest = (manifest: Partial<JigsawManifest> | null | undefined): JigsawManifest => {
  const images = Array.isArray(manifest?.images)
    ? manifest.images.map((record) => normalizeJigsawImageRecord(record))
    : [];

  return {
    version: 1,
    updatedAt: normalizeOptionalText(manifest?.updatedAt),
    images,
  };
};

export const normalizeJigsawAdminManifestResponse = (
  manifest: Partial<JigsawAdminManifestResponse> | null | undefined,
): JigsawAdminManifestResponse => {
  const images = Array.isArray(manifest?.images)
    ? manifest.images.map((record) => {
        const normalized = normalizeJigsawImageRecord(record);
        return {
          ...normalized,
          displayUrl: normalizeOptionalText(record.displayUrl),
          thumbnailUrl: normalizeOptionalText(record.thumbnailUrl),
          status: deriveJigsawImageStatus(normalized),
        };
      })
    : [];

  return {
    updatedAt: normalizeOptionalText(manifest?.updatedAt),
    images,
  };
};

export const buildFallbackJigsawCatalog = (): JigsawCatalog => ({
  dailyImageMap: { ...DEFAULT_JIGSAW_DAILY_IMAGE_MAP },
  dailyImageIds: Object.fromEntries(
    Object.keys(DEFAULT_JIGSAW_DAILY_IMAGE_MAP).map((dailyId) => [dailyId, `asset-map-${dailyId}`]),
  ),
  imageUrls: [...DEFAULT_JIGSAW_IMAGE_URLS],
});

export const normalizeJigsawPublicManifest = (
  manifest: Partial<JigsawPublicManifest> | null | undefined,
  assetBaseUrl?: string,
): JigsawCatalog => {
  const dailyImageMap: Record<string, string> = {};
  const dailyImageIds: Record<string, string> = {};
  const rawMap = manifest?.dailyImageMap;
  if (rawMap && typeof rawMap === 'object') {
    for (const [dailyId, imageUrl] of Object.entries(rawMap)) {
      if (isValidDailyId(dailyId) && typeof imageUrl === 'string' && imageUrl.trim()) {
        dailyImageMap[dailyId] = resolveJigsawPublicAssetUrl(imageUrl.trim(), assetBaseUrl);
      }
    }
  }

  const rawImageIds = manifest?.dailyImageIds;
  if (rawImageIds && typeof rawImageIds === 'object') {
    for (const [dailyId, imageId] of Object.entries(rawImageIds)) {
      if (isValidDailyId(dailyId) && typeof imageId === 'string' && imageId.trim()) {
        dailyImageIds[dailyId] = imageId.trim();
      }
    }
  }

  if (Array.isArray(manifest?.images)) {
    for (const image of manifest.images) {
      if (!image || typeof image !== 'object') continue;
      const imageId = normalizeOptionalText((image as { id?: string }).id);
      const assignedDates = Array.isArray((image as { assignedDates?: string[] }).assignedDates)
        ? (image as { assignedDates: string[] }).assignedDates
        : [];

      if (!imageId) continue;
      for (const dailyId of assignedDates) {
        if (isValidDailyId(dailyId)) {
          dailyImageIds[dailyId] = imageId;
        }
      }
    }
  }

  const fallbackCatalog = buildFallbackJigsawCatalog();
  if (Object.keys(dailyImageMap).length === 0) {
    return fallbackCatalog;
  }

  return {
    updatedAt: normalizeOptionalText(manifest?.updatedAt),
    dailyImageMap,
    dailyImageIds,
    imageUrls: Array.from(new Set(Object.values(dailyImageMap))),
  };
};

export const getPublicJigsawManifestUrl = () => {
  return getPublicEnvValue('BUN_PUBLIC_JIGSAW_MANIFEST_URL');
};

export const getPublicJigsawAssetBaseUrl = () => undefined;

export const hasPublicJigsawManifest = () => Boolean(getPublicJigsawManifestUrl());

export const getCachedJigsawCatalog = () => cachedCatalog;

export const clearJigsawCatalogCache = () => {
  cachedCatalog = null;
  catalogPromise = null;
};

const resolveJigsawPublicAssetUrl = (value: string, assetBaseUrl?: string) => {
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const baseUrl = assetBaseUrl?.trim()
    || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!baseUrl) {
    return value;
  }

  try {
    return new URL(value, baseUrl).toString();
  } catch {
    return value;
  }
};

const getDateFormatter = (timeZone: string) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

export const getJigsawTodayDailyId = (
  referenceDate = new Date(),
  timeZone = JIGSAW_SCHEDULE_TIMEZONE,
) => {
  const parts = getDateFormatter(timeZone).formatToParts(referenceDate);
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  if (!year || !month || !day) {
    return formatDailyId(referenceDate);
  }
  return `${year}-${month}-${day}`;
};

export const getRelativeJigsawDailyId = (
  offset: number,
  referenceDate = new Date(),
  timeZone = JIGSAW_SCHEDULE_TIMEZONE,
) => {
  const baseDailyId = getJigsawTodayDailyId(referenceDate, timeZone);
  const baseDate = parseDailyId(baseDailyId) ?? new Date(`${baseDailyId}T00:00:00`);
  baseDate.setDate(baseDate.getDate() + offset);
  return formatDailyId(baseDate);
};

export const getTomorrowJigsawDailyId = (
  referenceDate = new Date(),
  timeZone = JIGSAW_SCHEDULE_TIMEZONE,
) => getRelativeJigsawDailyId(1, referenceDate, timeZone);

export const getLockedJigsawDailyIds = (
  referenceDate = new Date(),
  timeZone = JIGSAW_SCHEDULE_TIMEZONE,
) =>
  Array.from({ length: JIGSAW_LOCKED_DAY_COUNT }, (_, offset) =>
    getRelativeJigsawDailyId(offset, referenceDate, timeZone),
  );

export const isLockedJigsawDailyId = (
  dailyId: string,
  referenceDate = new Date(),
  timeZone = JIGSAW_SCHEDULE_TIMEZONE,
) => getLockedJigsawDailyIds(referenceDate, timeZone).includes(dailyId);

export const getPinnedJigsawDates = (image: Pick<JigsawImageRecord, 'assignedDates' | 'pinnedDates'>) =>
  image.assignedDates.filter((dailyId) => image.pinnedDates.includes(dailyId));

export const getQueuedJigsawDates = (image: Pick<JigsawImageRecord, 'assignedDates' | 'pinnedDates'>) =>
  image.assignedDates.filter((dailyId) => !image.pinnedDates.includes(dailyId));

export const getNextSchedulableJigsawDailyId = (
  images: Array<Pick<JigsawImageRecord, 'assignedDates' | 'archived'>>,
  referenceDate = new Date(),
  timeZone = JIGSAW_SCHEDULE_TIMEZONE,
  maxDaysToScan = 366,
  startOffset = JIGSAW_LOCKED_DAY_COUNT,
) => {
  const occupiedDates = new Set(
    images
      .filter((image) => !image.archived)
      .flatMap((image) => image.assignedDates.filter(isValidDailyId)),
  );

  for (let offset = startOffset; offset < maxDaysToScan; offset += 1) {
    const dailyId = getRelativeJigsawDailyId(offset, referenceDate, timeZone);
    if (!occupiedDates.has(dailyId)) {
      return dailyId;
    }
  }

  return getRelativeJigsawDailyId(startOffset, referenceDate, timeZone);
};

export const getFallbackJigsawImageForDailyId = (dailyId: string): ResolvedJigsawImage => {
  const mappedImage = DEFAULT_JIGSAW_DAILY_IMAGE_MAP[dailyId];
  if (mappedImage) {
    return {
      dailyId,
      imageId: `asset-map-${dailyId}`,
      imageUrl: mappedImage,
      source: 'fallback',
    };
  }

  const fallbackCount = DEFAULT_JIGSAW_IMAGE_URLS.length;
  const fallbackIndex =
    fallbackCount > 0
      ? getDailyGameIndexForDailyId(dailyId) % fallbackCount
      : 0;
  const imageUrl = DEFAULT_JIGSAW_IMAGE_URLS[fallbackIndex] ?? '';

  return {
    dailyId,
    imageId: `asset-rotation-${fallbackIndex}`,
    imageUrl,
    source: 'fallback',
  };
};

export const resolveJigsawCatalogImageForDailyId = (
  catalog: JigsawCatalog | null | undefined,
  dailyId: string,
): ResolvedJigsawImage => {
  const imageUrl = catalog?.dailyImageMap?.[dailyId]?.trim();
  if (imageUrl) {
    return {
      dailyId,
      imageId: catalog?.dailyImageIds?.[dailyId] || `remote-${dailyId}`,
      imageUrl,
      source: 'remote',
    };
  }

  return getFallbackJigsawImageForDailyId(dailyId);
};

const isCacheStorageAvailable = () =>
  typeof window !== 'undefined' &&
  typeof window.caches !== 'undefined';

const getJigsawCacheRequest = (dailyId: string) =>
  new Request(`${JIGSAW_CACHE_REQUEST_ORIGIN}/daily/${encodeURIComponent(dailyId)}`);

const readJigsawImageCacheMetadata = (): JigsawImageCacheMetadata => {
  const rawValue = getLocalStorageItem(JIGSAW_IMAGE_CACHE_METADATA_KEY);
  if (!rawValue) return {};

  try {
    const parsed = JSON.parse(rawValue) as JigsawImageCacheMetadata;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
};

const writeJigsawImageCacheMetadata = (metadata: JigsawImageCacheMetadata) => {
  const entries = Object.entries(metadata).filter(([, entry]) =>
    entry &&
    typeof entry.imageUrl === 'string' &&
    Number.isFinite(entry.storedAt) &&
    Number.isFinite(entry.expiresAt),
  );

  if (entries.length === 0) {
    setLocalStorageItem(JIGSAW_IMAGE_CACHE_METADATA_KEY, null);
    return;
  }

  setLocalStorageItem(
    JIGSAW_IMAGE_CACHE_METADATA_KEY,
    JSON.stringify(Object.fromEntries(entries)),
  );
};

const isValidJigsawImageCacheEntry = (
  entry: JigsawImageCacheEntry | undefined,
  imageUrl: string,
  now = Date.now(),
) => {
  if (!entry) {
    return false;
  }

  return entry.imageUrl === imageUrl && entry.expiresAt > now;
};

export const evictExpiredJigsawImages = async (now = Date.now()) => {
  if (!isCacheStorageAvailable()) return;

  const metadata = readJigsawImageCacheMetadata();
  const cache = await window.caches.open(JIGSAW_IMAGE_CACHE_NAME);
  let didChange = false;

  for (const [dailyId, entry] of Object.entries(metadata)) {
    if (!entry || entry.expiresAt <= now) {
      await cache.delete(getJigsawCacheRequest(dailyId));
      delete metadata[dailyId];
      didChange = true;
    }
  }

  if (didChange) {
    writeJigsawImageCacheMetadata(metadata);
  }
};

export const ensureJigsawImagePersisted = async (
  dailyId: string,
  imageUrl: string,
): Promise<EnsureJigsawImagePersistedResult> => {
  if (!dailyId || !imageUrl) {
    return {
      dailyId,
      imageUrl,
      ok: false,
      fromCache: false,
      error: 'Missing daily id or image URL.',
    };
  }

  if (!isCacheStorageAvailable()) {
    return {
      dailyId,
      imageUrl,
      ok: false,
      fromCache: false,
      error: 'Persistent browser cache is unavailable.',
    };
  }

  await evictExpiredJigsawImages();

  const metadata = readJigsawImageCacheMetadata();
  const cache = await window.caches.open(JIGSAW_IMAGE_CACHE_NAME);
  const cacheRequest = getJigsawCacheRequest(dailyId);
  const existingEntry = metadata[dailyId];
  const cachedResponse = await cache.match(cacheRequest);

  if (cachedResponse && isValidJigsawImageCacheEntry(existingEntry, imageUrl)) {
    return {
      dailyId,
      imageUrl,
      ok: true,
      fromCache: true,
    };
  }

  await cache.delete(cacheRequest);
  delete metadata[dailyId];

  try {
    const response = await fetch(imageUrl, {
      method: 'GET',
      credentials: 'omit',
      mode: 'cors',
    });

    if (!response.ok) {
      return {
        dailyId,
        imageUrl,
        ok: false,
        fromCache: false,
        error: `Image download failed: ${response.status}`,
      };
    }

    await cache.put(cacheRequest, response.clone());

    const storedAt = Date.now();
    metadata[dailyId] = {
      imageUrl,
      storedAt,
      expiresAt: storedAt + JIGSAW_IMAGE_CACHE_TTL_MS,
    };
    writeJigsawImageCacheMetadata(metadata);

    return {
      dailyId,
      imageUrl,
      ok: true,
      fromCache: false,
    };
  } catch (error) {
    return {
      dailyId,
      imageUrl,
      ok: false,
      fromCache: false,
      error: error instanceof Error ? error.message : 'Image download failed.',
    };
  }
};

export const getPersistedJigsawImageObjectUrl = async (
  dailyId: string,
  imageUrl: string,
) => {
  if (!dailyId || !imageUrl || !isCacheStorageAvailable()) {
    return null;
  }

  await evictExpiredJigsawImages();

  const metadata = readJigsawImageCacheMetadata();
  if (!isValidJigsawImageCacheEntry(metadata[dailyId], imageUrl)) {
    return null;
  }

  const cache = await window.caches.open(JIGSAW_IMAGE_CACHE_NAME);
  const response = await cache.match(getJigsawCacheRequest(dailyId));
  if (!response) {
    return null;
  }

  const blob = await response.blob();
  if (!blob.size) {
    return null;
  }

  return URL.createObjectURL(blob);
};

export const prefetchJigsawDailyImages = async (
  catalog: JigsawCatalog | null | undefined,
  dailyIds: string[],
) =>
  Promise.all(
    Array.from(new Set(dailyIds.filter(isValidDailyId))).map((dailyId) => {
      const resolvedImage = resolveJigsawCatalogImageForDailyId(catalog, dailyId);
      return ensureJigsawImagePersisted(dailyId, resolvedImage.imageUrl);
    }),
  );

const withTimeout = async (url: string, timeoutMs = MANIFEST_TIMEOUT_MS) => {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timeoutId =
    controller && timeoutMs > 0
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

  try {
    return await fetch(url, {
      method: 'GET',
      signal: controller?.signal,
      credentials: 'omit',
      mode: 'cors',
    });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const loadJigsawCatalog = async (): Promise<JigsawCatalog> => {
  if (cachedCatalog) {
    return cachedCatalog;
  }

  if (catalogPromise) {
    return catalogPromise;
  }

  const manifestUrl = getPublicJigsawManifestUrl();
  if (!manifestUrl) {
    cachedCatalog = buildFallbackJigsawCatalog();
    return cachedCatalog;
  }

  const assetBaseUrl = getPublicJigsawAssetBaseUrl() || manifestUrl;

  catalogPromise = withTimeout(manifestUrl)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch jigsaw manifest: ${response.status}`);
      }

      const payload = (await response.json()) as JigsawPublicManifest;
      const catalog = normalizeJigsawPublicManifest(payload, assetBaseUrl);
      cachedCatalog = catalog;
      return catalog;
    })
    .finally(() => {
      catalogPromise = null;
    });

  return catalogPromise;
};
